import { usePage, router } from "@inertiajs/react";
import { useCallback } from "react";
import type { StreamSnapshot } from "@launch10/use-langgraph-stream";
import { useStreamSelector } from "@launch10/use-langgraph-stream";
import { type BrainstormGraphState, type InertiaProps } from "@shared";
import { useProjectStreamOptions } from "@hooks/useProjectStreamOptions";
import { useRootPath } from "~/stores/sessionStore";

type NewBrainstormProps =
  InertiaProps.paths["/projects/new"]["get"]["responses"]["200"]["content"]["application/json"];
type UpdateBrainstormProps =
  InertiaProps.paths["/projects/{uuid}/brainstorm"]["get"]["responses"]["200"]["content"]["application/json"];
type BrainstormPageProps = NewBrainstormProps | UpdateBrainstormProps;

export type BrainstormSnapshot = StreamSnapshot<BrainstormGraphState>;

/**
 * Check if URL already contains this threadId (prevents duplicate navigation)
 */
function isAlreadyAtThreadUrl(threadId: string): boolean {
  const urlThreadId = getThreadIdFromUrl();
  return urlThreadId === threadId;
}

function getThreadIdFromUrl(): string | undefined {
  // Brainstorm lives at /threads/:thread_id; keep the legacy
  // /projects/:uuid/brainstorm shape as a fallback for in-flight links.
  const threadMatch = window.location.pathname.match(/^\/threads\/([^/]+)$/);
  if (threadMatch) return threadMatch[1];
  const legacy = window.location.pathname.match(/^\/projects\/([^/]+)\/brainstorm$/);
  return legacy?.[1];
}

/**
 * Build stream options for the brainstorm chat.
 *
 * Uses useProjectStreamOptions for all the centralized config (JWT, apiUrl,
 * headers, threadId safety, reconnect, onUpdateEvent). Only adds the
 * brainstorm-specific threadId handler and URL-based threadId resolution.
 */
export function useBrainstormStreamOptions() {
  const page = usePage<BrainstormPageProps>();
  const { project } = page.props;
  const storeRootPath = useRootPath();
  const root_path = storeRootPath ?? (page.props as any).root_path;

  const onThreadId = useCallback((threadId: string) => {
    if (!threadId || threadId === "undefined" || threadId === "null") {
      console.error(
        "[useBrainstormChat] onThreadId called with invalid threadId:",
        threadId,
        new Error().stack
      );
      return;
    }

    if (isAlreadyAtThreadUrl(threadId)) return;

    // The brainstorm's thread_id is its stable home. project.uuid == this
    // thread_id (the deliberate overload), so the URL doubles as the project
    // address; the server resolves /threads/:id to the brainstorm chat.
    const newUrl = `/threads/${threadId}`;
    router.push({
      url: newUrl,
      preserveState: true,
      component: "Brainstorm",
      props: {
        ...page.props,
        thread_id: threadId,
        root_path,
        project: {
          ...project,
          uuid: threadId,
        },
      },
    });
  }, [page.props, root_path, project]);

  return useProjectStreamOptions<BrainstormGraphState>({
    assistantId: "brainstorm",
    onThreadId,
    getInitialThreadId: () => getThreadIdFromUrl() ?? (page.props.thread_id || undefined),
  });
}

// ---------------------------------------------------------------------------
// Selectors — must be used inside Chat.Root (SmartStreamProvider)
// ---------------------------------------------------------------------------

/**
 * Typed selector for brainstorm stream state.
 */
export function useBrainstormSelector<T>(
  selector: (snapshot: BrainstormSnapshot) => T
): T {
  return useStreamSelector<T, BrainstormGraphState>(selector);
}

export function useBrainstormMessages() {
  return useBrainstormSelector((s) => s.messages);
}

export function useBrainstormIsLoading() {
  return useBrainstormSelector((s) => s.isLoading);
}

export function useBrainstormIsThreadLoading() {
  return useBrainstormSelector((s) => s.isThreadLoading);
}

export function useBrainstormWebsiteId() {
  return useBrainstormSelector((s) => s.values?.websiteId);
}

/** True while the agent is compacting conversation history (see CompactingIndicator). */
export function useBrainstormIsCompacting() {
  return useBrainstormSelector((s) => s.values?.isCompacting ?? false);
}

/**
 * Returns whether this is a new conversation (should show landing page).
 */
export function useBrainstormIsNewConversation() {
  const { thread_id } = usePage<BrainstormPageProps>().props;
  const hasNoMessages = useBrainstormSelector((s) => s.messages.length === 0);
  if (thread_id) return false;
  return hasNoMessages;
}

/**
 * Returns whether the chat is currently streaming a response.
 */
export function useBrainstormIsStreaming() {
  return useBrainstormSelector((s) => s.isLoading);
}

